import { Fragment, useEffect, useState } from "react";
import clsx from "clsx";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { appSettingsConfig } from "@/lib/constants";

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children: React.ReactNode;
	rootElement?: HTMLElement;
}

export default function Modal({ isOpen, onClose, title, children, rootElement }: ModalProps) {
	const [styleVars, setStyleVars] = useState<any>({});

	useEffect(() => {
		const siteTitle = rootElement?.getAttribute("data-title") ?? "Jade Finance";
		setStyleVars(
			rootElement
				? {
						["--jF-theme-color" as any]:
							rootElement?.getAttribute("themeColor") ??
							appSettingsConfig[siteTitle].themeColor,
						["--jF-hover-color" as any]:
							rootElement?.getAttribute("hoverColor") ??
							appSettingsConfig[siteTitle].hoverColor,
						["--jF-theme-h-text" as any]:
							rootElement?.getAttribute("themeHeaderTxt") ??
							appSettingsConfig[siteTitle].themeHeaderTxt,
						["--jF-bullet-bg" as any]:
							rootElement?.getAttribute("bulletBG") ??
							appSettingsConfig[siteTitle].bulletBG,
						["--jF-breakdown-bg-color" as any]:
							rootElement?.getAttribute("breakdown-bg-color") ??
							appSettingsConfig[siteTitle].breakdownBG,
				  }
				: {}
		);
	}, [rootElement]);

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog
				as="div"
				className="jF-relative jF-z-[9999] jF-container" //Important: jF-container is required for the modal to be styled correctly
				onClose={onClose}
				style={{
					...(styleVars as React.CSSProperties),
					position: "relative",
					zIndex: 9999,
				}}
			>
				<TransitionChild
					as={Fragment}
					enter="jF-ease-out jF-duration-300"
					enterFrom="jF-opacity-0"
					enterTo="jF-opacity-100"
					leave="jF-ease-in jF-duration-200"
					leaveFrom="jF-opacity-100"
					leaveTo="jF-opacity-0"
				>
					<div className="jF-fixed jF-inset-0 jF-bg-black jF-bg-opacity-50" />
				</TransitionChild>

				<div className="jF-fixed jF-inset-0 jF-overflow-y-auto">
					<div className="jF-flex jF-min-h-full jF-items-center jF-justify-center jF-p-4 jF-text-center">
						<TransitionChild
							as={Fragment}
							enter="jF-ease-out jF-duration-300"
							enterFrom="jF-opacity-0 jF-scale-95"
							enterTo="jF-opacity-100 jF-scale-100"
							leave="jF-ease-in jF-duration-200"
							leaveFrom="jF-opacity-100 jF-scale-100"
							leaveTo="jF-opacity-0 jF-scale-95"
						>
							<DialogPanel className="jF-w-full md:jF-max-w-[30rem] lg:jF-max-w-[35rem] xl:jF-max-w-[44rem] jF-transform jF-overflow-hidden jF-bg-white jF-p-0 sm:jF-p-6 jF-text-left jF-align-middle jF-shadow-xl jF-transition-all jF-rounded-2xl">
								<div
									className={clsx(
										"jF-flex jF-items-center jF-justify-between",
										title ? "jF-mb-4" : ""
									)}
								>
									{title && (
										<DialogTitle
											as="h3"
											className="jF-text-lg jF-font-medium jF-leading-6 jF-text-gray-900"
										>
											{title}
										</DialogTitle>
									)}
									<button
										id="modalCloseBtn"
										type="button"
										className="jF-flex jF-items-center jF-justify-center jF-text-gray-400 hover:jF-text-gray-600 focus:jF-outline-none focus:!jF-ring-2 focus:jF-ring-[--jF-theme-color] focus:jF-ring-offset-2 jF-absolute jF-top-4 jF-right-6 jF-z-20 jF-bg-gray-200 jF-rounded-full jF-p-2"
										onClick={onClose}
									>
										<span className="jF-sr-only">Close</span>
										<XMarkIcon
											className="jF-h-6 jF-w-6 jF-text-gray-400"
											aria-hidden="true"
										/>
									</button>
								</div>

								<div className="jF-max-h-100 jF-overflow-y-auto jF-pr-0 sm:jF-pr-2">
									{children}
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
